import type { AxiosRequestConfig, Method } from 'axios';
import axios from 'axios';

import isString from 'lodash/isString';
import queryUtils from 'app/shared/utils/queryUtils';
import type { QueryObject } from 'app/shared/utils/queryUtils';
import includes from 'lodash/includes';
import trimStart from 'lodash/trimStart';
import trimEnd from 'lodash/trimEnd';

import AuthActions from 'app/shared/flux/actions/AuthActions';
import getRequestHeaders from 'app/shared/utils/httpHeaders';
import RequesterActions from 'app/shared/flux/actions/RequesterActions';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { ContextError, logError } from '@zg-rentals/log-error';

const logger = getGlobalLogger('utils/requester');
const PX_BLOCKED_HEADER_KEY = 'x-px-blocked';

export enum RequesterErrorCategory {
  ApiData = 'API_DATA_ERROR',
  PxBlocked = 'PX_BLOCKED_ERROR',
  Network = 'NETWORK_ERROR',
  Http = 'HTTP_ERROR',
  Unknown = 'UNKNOWN_ERROR',
}

if (__SERVER__) {
  const https = require('https');
  axios.defaults.httpsAgent = new https.Agent({ keepAlive: true });
}

export const shouldIncludeRequestBody = (method: string): boolean => {
  return method.toLowerCase() !== 'get';
};

export const buildEndpointUrl = (baseUrl: string, path: string): string => {
  return `${trimEnd(baseUrl, '/')}/${trimStart(path, '/')}`;
};

export const determineBaseUrl = (
  hostname: string | undefined,
  isDev: boolean,
  skipProxy: boolean,
  isServer: boolean,
  apiEnv?: string,
): string => {
  if (isServer) {
    return trimEnd(apiEnv || '', '/');
  }

  if (!hostname) {
    return '';
  }

  const isSherman = hostname.endsWith('.testpads.net') && !hostname.startsWith('comet1.');
  if (isDev || isSherman || !skipProxy) {
    return '/node';
  }
  return '';
};

export const categorizeError = (error: any): RequesterErrorCategory => {
  if (!error) {
    return RequesterErrorCategory.Unknown;
  }

  if (error?.response?.headers?.[PX_BLOCKED_HEADER_KEY]) {
    return RequesterErrorCategory.PxBlocked;
  } else if (error.isAxiosError && !error.response) {
    return RequesterErrorCategory.Network;
  } else if (error.isAxiosError && error.response) {
    return RequesterErrorCategory.Http;
  } else if (error.category) {
    return error.category;
  }
  return RequesterErrorCategory.Unknown;
};

interface EnhancedQueryParams {
  trace?: boolean;
  traceId?: string;
  [key: string]: any;
}

export const enhanceQueryParams = (
  queryParams: Record<string, any>,
  userRoles: Array<string> | undefined,
  isServer: boolean,
  traceId?: string,
): EnhancedQueryParams => {
  const enhanced: EnhancedQueryParams = { ...queryParams };

  if (userRoles && includes(userRoles, 'rep')) {
    enhanced.trace = true;
  }

  if (isServer && traceId) {
    enhanced.traceId = traceId;
  }

  return enhanced;
};

interface RequestOptions extends AxiosRequestConfig {
  method: Method;
  url: string;
  params: Record<string, any>;
  paramsSerializer: (params: Record<string, any>) => string;
  headers: Record<string, string>;
  timeout: number;
  signal?: AbortSignal;
  data?: any;
}

export const buildRequestOptions = (
  method: Method,
  endpointUrl: string,
  queryParams: Record<string, any>,
  requestHeaders: Record<string, string>,
  body: any,
  signal: AbortSignal | null,
): RequestOptions => {
  const shouldIncludeBody = shouldIncludeRequestBody(method);

  const requestOptions: RequestOptions = {
    method,
    url: endpointUrl,
    params: queryParams,
    paramsSerializer: (params: Record<string, any>) =>
      `${new URLSearchParams(
        Object.entries(params).reduce((acc: Record<string, any>, [key, value]) => {
          if (value != null) {
            acc[key] = Array.isArray(value) ? value.join(',') : value;
          }
          return acc;
        }, {}),
      )}`,
    headers: requestHeaders,
    timeout: 15000,
    signal: signal || undefined,
  };

  if (shouldIncludeBody) {
    requestOptions.data = body;
  }

  return requestOptions;
};

interface RunscopeLogData {
  method: string;
  url: string;
  params: Record<string, any>;
  headers: Record<string, string>;
  data?: any;
  traceId: string;
  requestId: string;
  msg: string;
  response?: any;
}

export const createRunscopeLogData = (
  method: string,
  endpointUrl: string,
  queryParams: Record<string, any>,
  requestHeaders: Record<string, string>,
  body: any,
  traceId: string,
  requestId: string,
  msg: string,
  response?: any,
): RunscopeLogData => {
  const shouldIncludeBody = shouldIncludeRequestBody(method);

  const logData: RunscopeLogData = {
    method,
    url: endpointUrl,
    params: queryParams,
    headers: requestHeaders,
    data: shouldIncludeBody ? body : undefined,
    traceId,
    requestId,
    msg,
  };

  if (response) {
    logData.response = response;
  }

  return logData;
};

export const processApiError = (
  apiError: any,
  responseData: any,
  responseHeaders: any,
  status: number,
  statusText: string,
) => {
  let errorToThrow;
  if (typeof apiError !== 'object' || apiError === null) {
    errorToThrow = new Error(String(apiError));
  } else {
    errorToThrow = apiError;
  }

  errorToThrow.category = RequesterErrorCategory.ApiData;

  errorToThrow.responseContext = {
    data: responseData,
    headers: responseHeaders,
    status,
    statusText,
  };

  return errorToThrow;
};

export const normalizeResponseData = (responseData: any): any => {
  if (isString(responseData)) {
    return {};
  }
  return responseData;
};

export const isComscore = (url: string | undefined): boolean => {
  return url ? url.indexOf('/api/comscore') > -1 : false;
};

export const shouldSetCookies = (isServer: boolean, responseHeaders: any, path: string): boolean => {
  return !!(isServer && responseHeaders && includes(path, 'v2/user/'));
};

export const shouldUpdateSessionToken = (
  currentToken: string | undefined | null,
  newToken: string | undefined | null,
): boolean => {
  return !!(newToken && (!currentToken || currentToken !== newToken));
};

export const shouldCheckValidSession = (responseData: any): boolean => {
  return responseData.loggedIn === false; // must be strictly === false
};

export const createRequestEntry = (startTime: number, endTime: number, url: string) => {
  return {
    startTime,
    endTime,
    duration: endTime - startTime,
    url,
  };
};

export const createDevLogData = (startTime: number, endTime: number, url: string, traceId: string) => {
  return {
    endTime,
    startTime,
    duration: endTime - startTime,
    url,
    traceId,
  };
};

interface PxBlockedRequestDetails {
  method: string;
  url: string;
  params: Record<string, any>;
  headers: Record<string, string>;
  data: string;
}

export const buildPxBlockedRequestDetails = (
  method: string,
  endpointUrl: string,
  queryParams: Record<string, any>,
  requestHeaders: Record<string, string>,
  body: any,
): PxBlockedRequestDetails => {
  return {
    method,
    url: endpointUrl,
    params: queryParams,
    headers: requestHeaders,
    data: JSON.stringify(body),
  };
};

export const buildFullUrl = (endpointUrl: string, queryParams: QueryObject): string => {
  return endpointUrl + queryUtils.stringify(queryParams);
};

interface RequesterParams {
  method?: Method;
  path: string;
  queryParams?: Record<string, any>;
  body?: any;
  header?: Record<string, string>;
  abortControllerSignal?: AbortSignal | null;
  skipProxy?: boolean;
}

export default function requester({
  method = 'get',
  path,
  queryParams = {},
  body = {},
  header = {},
  abortControllerSignal: signal = null,
  skipProxy = true,
}: RequesterParams) {
  return async (dispatch: any, getState: () => any) => {
    const startTime = new Date().getTime();

    const state = getState();
    const { location, user, app } = state;
    const traceId = location.ssrEntry.traceId;

    const baseUrl = determineBaseUrl(
      typeof window !== 'undefined' ? window.location.hostname : undefined,
      __DEV__,
      skipProxy,
      __SERVER__,
      process.env.ZG_API,
    );

    const endpointUrl = buildEndpointUrl(baseUrl, path);

    const enhancedQueryParams = enhanceQueryParams(queryParams, user.info?.roles, __SERVER__, traceId);

    const requestHeaders = getRequestHeaders(header, {
      httpMethod: method,
      originalUri: location.current?.pathname,
      requestId: app.requestId,
      ip: app.ip,
      csrfToken: user.csrfToken,
      userToken: user.userToken,
      location,
      userAgent: app.userAgent,
      serverSideCookies: user.serverSideCookies,
      logger,
    });

    const requestOptions = buildRequestOptions(method, endpointUrl, enhancedQueryParams, requestHeaders, body, signal);

    const isRunscopeAgent = app.userAgent && app.userAgent.includes('runscope-radar');

    if (isRunscopeAgent) {
      const logData = createRunscopeLogData(
        method,
        endpointUrl,
        enhancedQueryParams,
        requestHeaders,
        body,
        traceId,
        app.requestId,
        'Before API is called...',
      );
      logger?.warn(logData);
    }

    try {
      const response = await axios(requestOptions);

      const endTime = new Date().getTime();
      const url = buildFullUrl(endpointUrl, enhancedQueryParams);

      response.data = normalizeResponseData(response.data);

      if (isComscore(response.config.url)) {
        return true;
      }

      if (__DEV__) {
        const devLogData = createDevLogData(startTime, endTime, url, location.ssrEntry.traceId);
        logger?.warn(devLogData);
      }

      const apiError = response.data.error;
      if (apiError) {
        if (isRunscopeAgent) {
          const logData = createRunscopeLogData(
            method,
            endpointUrl,
            enhancedQueryParams,
            requestHeaders,
            body,
            traceId,
            app.requestId,
            'After API is called - ERROR...',
            response.data,
          );
          logger?.warn(logData);
        }

        const errorToThrow = processApiError(
          apiError,
          response.data,
          response.headers,
          response.status,
          response.statusText,
        );

        throw errorToThrow;
      }

      if (isRunscopeAgent) {
        const logData = createRunscopeLogData(
          method,
          endpointUrl,
          enhancedQueryParams,
          requestHeaders,
          body,
          traceId,
          app.requestId,
          'After API is called...',
          response.data,
        );
        logger?.warn(logData);
      }

      if (__SERVER__) {
        const entry = createRequestEntry(startTime, endTime, url);
        dispatch(RequesterActions.addRequestToSsrTrace(entry));
      }

      if (shouldSetCookies(__SERVER__, response.headers, path)) {
        if (response.headers['set-cookie']) {
          dispatch(RequesterActions.saveServerSideResponseCookies(response));
          dispatch(RequesterActions.saveServerSideSetCookieArray(response.headers['set-cookie']));
        }

        dispatch(RequesterActions.updateCsrfToken(response.data.creds));
      }

      if (!__SERVER__) {
        dispatch(RequesterActions.updateCsrfToken(response.data.creds));

        if (shouldCheckValidSession(response.data)) {
          dispatch(AuthActions.login.checkValidSession());
        }
      }

      if (response.data && response.data.creds) {
        const latestSessionToken = response.data.creds.sessionToken;
        if (shouldUpdateSessionToken(user.sessionToken, latestSessionToken)) {
          dispatch(RequesterActions.updateSessionToken(latestSessionToken));
        }
      }

      return response.data;
    } catch (error) {
      if (error?.response?.headers?.[PX_BLOCKED_HEADER_KEY]) {
        const requestDetails = buildPxBlockedRequestDetails(
          method,
          endpointUrl,
          enhancedQueryParams,
          requestHeaders,
          body,
        );
        logError({
          error: new ContextError('PxBlockedError', {
            details: 'Request blocked by PerimeterX',
            ...requestDetails,
          }),
          httpRequest: requestDetails,
        });
      }

      error.category = categorizeError(error);
      throw error;
    }
  };
}
